import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Edit2, Trash2, Check, X } from 'lucide-react';
import { doc, updateDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { Input, Button } from '../ui';
import { useAuth } from '../../contexts/AuthContext';
import { useFeatureUpgradeModal } from '../../contexts/FeatureUpgradeModalContext';
import { useToast } from '../../hooks/useToast';
import { db, app } from '../../lib/firebase';
import { BillingProfile } from '../../types/firestoreTypes';
import AddBillingProfileModal from './AddBillingProfileModal';
import EditBillingProfileModal from './EditBillingProfileModal';
import DeleteConfirmationModal from './DeleteConfirmationModal';

const BillingProfilesTab: React.FC = () => {
  const { t } = useTranslation();
  const { user, therapist } = useAuth();
  const { openUpgradeModal } = useFeatureUpgradeModal();
  const { addToast } = useToast();
  const functions = getFunctions(app, 'europe-west1');

  // State variables
  const [profiles, setProfiles] = useState<BillingProfile[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [errorDetails, setErrorDetails] = useState<string | null>(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedProfileForEdit, setSelectedProfileForEdit] = useState<BillingProfile | null>(null);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [profileToDeleteId, setProfileToDeleteId] = useState<string | null>(null);
  const [profileToDeleteName, setProfileToDeleteName] = useState<string | null>(null);
  const [isDeletingProfile, setIsDeletingProfile] = useState(false);

  // Load profiles from therapist data
  useEffect(() => {
    if (therapist) {
      setIsLoading(true);
      setErrorKey(null);
      setErrorDetails(null);

      try {
        if (therapist.billingProfiles) {
          const profilesArray = Object.entries(therapist.billingProfiles).map(
            ([id, profile]) => ({
              ...profile,
              profileId: id,
            })
          );
          setProfiles(profilesArray);
        } else {
          setProfiles([]);
        }
      } catch (err) {
        console.error('Error processing billing profiles:', err);
        setErrorKey('settings.billing.errors.loadFailed');
      } finally {
        setIsLoading(false);
      }
    }
  }, [therapist, isAddModalOpen, isEditModalOpen, isDeleteConfirmOpen]);

  const handleAddProfile = () => {
    // --- Feature Limit Check ---
    const billingProfileLimit = therapist?.packageFeaturesSnapshot?.BILLING_PROFILE_LIMIT;
    const currentProfileCount = profiles.length;

    // Check if billingProfileLimit is a number and if the limit has been reached.
    // If billingProfileLimit is not defined or not a number, or if limit is 0 (treat as potentially unlimited/misconfigured), allow creation.
    if (typeof billingProfileLimit === 'number' && billingProfileLimit > 0 && currentProfileCount >= billingProfileLimit) {
      openUpgradeModal({
        featureNameKey: 'featureUpgradeModal.featureNames.billingProfileLimit',
        customMessageKey: 'featureUpgradeModal.messages.billingProfileLimitReached',
      });
      return; // Stop before opening the modal
    }
    // --- End: Feature Limit Check ---

    setIsAddModalOpen(true);
  };

  const handleEditProfile = (profileId: string) => {
    const profileToEdit = profiles.find(profile => profile.profileId === profileId);
    if (profileToEdit) {
      setSelectedProfileForEdit(profileToEdit);
      setIsEditModalOpen(true);
    } else {
      console.error('Profile not found:', profileId);
    }
  };

  const handleDeleteProfile = (profile: BillingProfile) => {
    setProfileToDeleteId(profile.profileId);
    setProfileToDeleteName(profile.profileName);
    setIsDeleteConfirmOpen(true);
  };

  const confirmDeleteProfile = async () => {
    if (!profileToDeleteId) {
      console.error('No profile selected for deletion');
      return;
    }

    setIsDeletingProfile(true);
    try {
      const callDeleteBillingProfile = httpsCallable(functions, 'deleteBillingProfile');
      await callDeleteBillingProfile({ profileId: profileToDeleteId });

      addToast({
        type: 'success',
        message: t('settings.billing.profiles.success.profileDeleted', {
          profileName: profileToDeleteName,
        }),
      });

      setIsDeleteConfirmOpen(false);
      setProfileToDeleteId(null);
      setProfileToDeleteName(null);
    } catch (error: any) {
      console.error('Error deleting profile:', error);
      const errorMessage = error.message || t('settings.billing.profiles.errors.deleteFailedDefault');
      addToast({
        type: 'error',
        message: t('settings.billing.profiles.errors.deleteFailed', {
          profileName: profileToDeleteName,
          error: errorMessage,
        }),
      });
    } finally {
      setIsDeletingProfile(false);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-4 border-[#7F56D9] border-t-transparent"></div>
        <span className="ml-3 text-gray-600">
          {t('settings.billing.status.loading')}
        </span>
      </div>
    );
  }

  // Error state
  if (errorKey) {
    return (
      <div className="p-4 bg-red-50 rounded-lg text-red-700 border border-red-200">
        <p className="font-medium">{t('settings.billing.errors.title')}</p>
        <p>{t(errorKey, { details: errorDetails })}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-900">
          {t('settings.billing.profiles.heading')}
        </h2>
        <Button
          variant="primary"
          onClick={handleAddProfile}
          leftIcon={<Plus className="w-4 h-4" />}
        >
          {t('settings.billing.profiles.buttons.add')}
        </Button>
      </div>

      {/* Profiles List */}
      {profiles.length === 0 ? (
        <div className="bg-gray-50 rounded-lg p-8 text-center">
          <p className="text-gray-600">
            {t('settings.billing.profiles.empty')}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {profiles.map((profile) => (
            <div
              key={profile.profileId}
              className="bg-white rounded-lg border border-gray-200 p-6"
            >
              <div className="flex justify-between items-start">
                <div className="space-y-2">
                  <h3 className="text-lg font-medium text-gray-900">
                    {profile.profileName}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {t('settings.billing.profiles.provider', {
                      provider: profile.provider,
                    })}
                  </p>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-600">
                      {t('settings.billing.profiles.apiKeyStatus')}:
                    </span>
                    {profile.isApiKeySet ? (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-sm bg-green-50 text-green-700">
                        <Check className="w-4 h-4 mr-1" />
                        {t('settings.billing.profiles.apiKeySet')}
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-sm bg-red-50 text-red-700">
                        <X className="w-4 h-4 mr-1" />
                        {t('settings.billing.profiles.apiKeyNotSet')}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex space-x-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => handleEditProfile(profile.profileId)}
                    leftIcon={<Edit2 className="w-4 h-4" />}
                  >
                    {t('settings.billing.profiles.buttons.edit')}
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => handleDeleteProfile(profile)}
                    leftIcon={<Trash2 className="w-4 h-4" />}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    {t('settings.billing.profiles.buttons.delete')}
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <AddBillingProfileModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSaveSuccess={() => {
          setIsAddModalOpen(false);
          // TODO: Refresh profiles list
        }}
      />

      <EditBillingProfileModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedProfileForEdit(null);
        }}
        onSaveSuccess={() => {
          setIsEditModalOpen(false);
          setSelectedProfileForEdit(null);
          // TODO: Refresh profiles list
        }}
        profileToEdit={selectedProfileForEdit}
      />

      <DeleteConfirmationModal
        isOpen={isDeleteConfirmOpen}
        onClose={() => {
          if (!isDeletingProfile) {
            setIsDeleteConfirmOpen(false);
            setProfileToDeleteId(null);
            setProfileToDeleteName(null);
          }
        }}
        onConfirm={confirmDeleteProfile}
        profileName={profileToDeleteName}
        isDeleting={isDeletingProfile}
      />
    </div>
  );
};

export default BillingProfilesTab;
